import { useCallback } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { IconButton, useMediaQuery } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import { Period } from 'components/AnalyticsPage/Periods';
import { TrapType } from 'components/AnalyticsPage/TrapType';
import Toggle from 'components/Common/Toggle';
import './../../ChartTitle/ChartTitle.scss';
import { MediaBreakpoints } from 'hooks/Screen/useScreenSize';

interface MiceActivityChartTitleProps {
  title: string;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  showCustom: boolean;
  setShowCustom: (value: boolean) => void;
  period: Period;
  setPeriod: (value: Period) => void;
  refetch: () => void;
  showSwitchTrapType?: boolean;
  switchTrapType?: TrapType;
  setSwitchTrapType?: (value: TrapType) => void;
  switchTrapTypeLoading?: boolean;
}

const MiceActivityChartTitle: React.FC<MiceActivityChartTitleProps> = ({
  title,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  showCustom,
  setShowCustom,
  period,
  setPeriod,
  refetch,
  showSwitchTrapType = false,
  switchTrapType = TrapType.MCT,
  setSwitchTrapType,
  switchTrapTypeLoading = false,
}) => {
  const handlePeriodChange = useCallback(
    (newPeriod: Period) => {
      setPeriod(newPeriod);
      if (newPeriod === Period.YEAR) {
        const oneYearAgo = dayjs().subtract(1, 'year').format('YYYY-MM-DD');
        const today = dayjs().format('YYYY-MM-DD');
        setStartDate(oneYearAgo);
        setEndDate(today);
        setShowCustom(false);
        setTimeout(() => refetch(), 100);
      } else if (newPeriod === Period.THREE_MONTH) {
        const threeMonthsAgo = dayjs().subtract(3, 'month').format('YYYY-MM-DD');
        const today = dayjs().format('YYYY-MM-DD');
        setStartDate(threeMonthsAgo);
        setEndDate(today);
        setShowCustom(false);
        setTimeout(() => refetch(), 100);
      } else if (newPeriod === Period.MONTH) {
        const oneMonthAgo = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
        const today = dayjs().format('YYYY-MM-DD');
        setStartDate(oneMonthAgo);
        setEndDate(today);
        setShowCustom(false);
        setTimeout(() => refetch(), 100);
      } else if (newPeriod === Period.CUSTOM) {
        setShowCustom(true);
      }
    },
    [setStartDate, setEndDate, setShowCustom, setPeriod, refetch],
  );
  const { t } = useTranslation();
  const mobile = useMediaQuery(MediaBreakpoints.mobile);

  return (
    <div className="d-flex flex-column justify-content-between gap-2">
      <div
        className={`d-flex position-relative ${mobile && showSwitchTrapType ? 'justify-content-start' : 'justify-content-center'}`}
      >
        <div className="d-flex align-items-center">
          <h4 className="text-truncate m-0">{title}</h4>
        </div>
        {showSwitchTrapType && setSwitchTrapType && (
          <div className="toggle_trap">
            <Toggle
              checked={switchTrapType === TrapType.SMST}
              onChange={() => {
                if (switchTrapTypeLoading) {
                  enqueueSnackbar(t('ChartsAnalitics.all_traps_activity_chart.data_is_loading'), {
                    variant: 'warning',
                    action: key => (
                      <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                        <CloseIcon />
                      </IconButton>
                    ),
                  });
                } else {
                  setSwitchTrapType(
                    switchTrapType === TrapType.SMST
                      ? (switchTrapType = TrapType.MCT)
                      : (switchTrapType = TrapType.SMST),
                  );
                }
              }}
              label={t('ChartsAnalitics.all_traps_activity_chart.toggle_label_right')}
              leftLabel={t('ChartsAnalitics.all_traps_activity_chart.toggle_label_left')}
              size="sm"
              className="flex align-items-end"
            />
          </div>
        )}
      </div>

      <div className="d-flex flex-wrap flex-md-nowrap justify-content-center">
        {showCustom && (
          <div className="d-flex align-items-center">
            <div className="m-1">
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  setTimeout(() => refetch(), 100);
                }}
              />
            </div>
            <div className="m-1">
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={e => {
                  setEndDate(e.target.value);
                  setTimeout(() => refetch(), 100);
                }}
              />
            </div>
          </div>
        )}
        <button
          className={`btn waves-effect waves-light ${period === Period.CUSTOM ? 'active-toggle' : ''}`}
          onClick={() => handlePeriodChange(Period.CUSTOM)}
        >
          {t('ChartsAnalitics.filters.custom')}
        </button>
        <button
          className={`btn waves-effect waves-light ${period === Period.YEAR ? 'active-toggle' : ''}`}
          onClick={() => handlePeriodChange(Period.YEAR)}
          disabled={period === Period.YEAR}
        >
          {t('ChartsAnalitics.filters.year')}
        </button>
        <button
          className={`btn waves-effect waves-light ${period === Period.THREE_MONTH ? 'active-toggle' : ''}`}
          onClick={() => handlePeriodChange(Period.THREE_MONTH)}
          disabled={period === Period.THREE_MONTH}
        >
          {t('ChartsAnalitics.filters.3months')}
        </button>
        <button
          className={`btn waves-effect waves-light ${period === Period.MONTH ? 'active-toggle' : ''}`}
          onClick={() => handlePeriodChange(Period.MONTH)}
          disabled={period === Period.MONTH}
        >
          {t('ChartsAnalitics.filters.month')}
        </button>
      </div>
    </div>
  );
};

export default MiceActivityChartTitle;
